import { PROVIDER_DISPLAY_NAMES, type ServerProviderStatus } from "@taskin-code/contracts";
import { memo } from "react";
import { Alert, AlertDescription, AlertTitle } from "../ui/alert";
import { Button } from "../ui/button";
import { CircleAlertIcon } from "~/lib/icons";

/**
 * Command that renews this provider's credentials, as published by the server.
 *
 * The client deliberately does not spell these commands itself: only the server
 * knows where each provider's credentials live and where its real binary is.
 * Terminals run with a managed wrapper earlier on `PATH` that redirects
 * `CODEX_HOME`, so a client-side guess would save the login somewhere the
 * provider never reads.
 */
function reauthCommandForStatus(status: ServerProviderStatus): string | null {
  return status.reauthCommand ?? null;
}

export const ProviderHealthBanner = memo(function ProviderHealthBanner({
  status,
  onReauthenticate,
  isReauthenticating = false,
}: {
  status: ServerProviderStatus | null;
  onReauthenticate?: (command: string) => void;
  isReauthenticating?: boolean;
}) {
  if (!status || status.status === "ready") {
    return null;
  }

  const providerLabel = PROVIDER_DISPLAY_NAMES[status.provider] ?? status.provider;
  const defaultMessage =
    status.status === "error"
      ? `${providerLabel} provider is unavailable.`
      : `${providerLabel} provider has limited availability.`;
  const title = `${providerLabel} provider status`;

  // Expired or missing credentials are the one provider failure the operator
  // can fix from here, so that case — and only that case — gets an action.
  const reauthCommand =
    status.authStatus === "unauthenticated" ? reauthCommandForStatus(status) : null;
  const canReauthenticate = reauthCommand !== null && onReauthenticate !== undefined;

  return (
    <div className="pt-3 mx-auto max-w-3xl">
      <Alert variant={status.status === "error" ? "error" : "warning"}>
        <CircleAlertIcon />
        <AlertTitle>{title}</AlertTitle>
        <AlertDescription className="flex flex-wrap items-center justify-between gap-2">
          <span className="line-clamp-3" title={status.message ?? defaultMessage}>
            {status.message ?? defaultMessage}
          </span>
          {canReauthenticate ? (
            <Button
              size="sm"
              variant="outline"
              disabled={isReauthenticating}
              onClick={() => onReauthenticate(reauthCommand)}
            >
              {isReauthenticating ? "Opening terminal…" : "Re-authenticate"}
            </Button>
          ) : null}
        </AlertDescription>
      </Alert>
    </div>
  );
});
